import {
  AmplifyHub,
  AnalyticsService,
  AnalyticsTrackingEvent,
  BrandclubStoreConfigUtils,
  buildAppInstallationInfo,
  IAppInstallationInfo,
  initializeAmplifyAuth,
  Logger,
  useFingerprintJSContext,
} from '@brandclub/common-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PropsWithChildren, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { appConfigQueryOptions, useAppConfig, useBrandAppConfigWithFallback } from './api/cache/appConfigQueryOptions';
import { appThemeQueryOptions } from './api/cache/appThemeQueryOptions';
import { activeCartsQueryOptions, dropCart, useCartCount } from './api/cache/cartQueryOptions';
import {
  brandclubProductQueryOptions,
  useCurrentBrandclubProduct,
  useDtcSiteProduct,
} from './api/cache/dtcProductQueryOptions';
import { usePersistedQueryManager } from './api/cache/PersistedQueryManager';
import { updateAppInstallationInfoInCheckoutBar } from './api/rest/user';
import { useCurrentStacklineSku } from './hooks/useCurrentStacklineSku';
import { markAuthReady } from './utils/authReady';
import { useTrackActions, useTrackViewEvent } from './hooks/useTracking';
import { userProfileQueryOptions, useUserProfile } from './hooks/useUserProfile';
import { useAppDispatch } from './redux/hooks';
import { updateVisitorTracking } from './redux/reducers/visitorTracking';
import { removeFunctionFields } from './utils';
import { HOST_DOMAIN } from './utils/domain';
import { UserProfile } from '@brandclub/types';
import { isCheckoutAnalyticsSuppressed } from './analytics/policy';
import { getHostMixpanelAdoptions, subscribeHostMixpanel } from './analytics/hostMixpanel';

const logger = new Logger('AppInitializer');

const initializeMixpanel = (domain: string) => {
  try {
    // The host owns analytics (`suppressAnalytics`) → NEVER stand up this bundle's
    // own SDK instance. Adoption (`adoptMixpanelInstance`, from the host's mount
    // handoff) is the ONLY way this bundle gets an instance on such a host; if the
    // handoff hasn't landed yet, initializing here would create a SECOND instance
    // with its own `api_host`/batcher and a duplicate autocapture page-view — the
    // very double-send this flag exists to prevent. Adoption already no-ops this
    // via `isMixpanelInitialized()`; this makes the skip order-INDEPENDENT so a
    // late handoff can't slip a rogue instance in first. (Concierge fires on user
    // interaction, long after the mount-time adoption, so it still publishes
    // through the shared instance.)
    if (isCheckoutAnalyticsSuppressed()) return;
    if (!AnalyticsService.isMixpanelInitialized()) {
      AnalyticsService.initializeMixpanel(domain);
      logger.debug('Mixpanel initialized early');
    }
  } catch (e) {
    logger.error('Early Mixpanel initialization failed', e);
  }
};

const initAppInstallationInfo = async (userId: string | undefined, signal?: AbortSignal) => {
  if (!userId) {
    return;
  }
  try {
    const appId = BrandclubStoreConfigUtils.getWebAppId();
    const appInstallationInfo: IAppInstallationInfo = await buildAppInstallationInfo({
      userId,
      appId,
    });
    if (appInstallationInfo && !signal?.aborted) {
      await updateAppInstallationInfoInCheckoutBar(appInstallationInfo, signal);
    }
  } catch (e) {
    // Only log non-abort errors
    if (e instanceof Error && (e.name === 'AbortError' || e.message.includes('abort'))) {
      logger.debug('App installation info initialization was aborted');
      return;
    }
    logger.error('An error occurred when building appInstallationInfo', e);
  }
};

// Combined tracking component.
//
// Emission suppression (for hosts that own analytics themselves, e.g. the Pages
// storefront) is handled centrally by the `useTracking` `track` wrapper via the
// `suppressAnalytics` mount flag (see analytics/policy.ts) — so BOTH the page-view
// below and every action event no-op on such a host, and no per-call guard is
// needed here.
const MixpanelTracking: React.FC<{ isAuthReady: boolean }> = ({ isAuthReady }) => {
  const dispatch = useAppDispatch();
  const visitorTracking = useFingerprintJSContext();
  const currentStacklineSku = useCurrentStacklineSku();
  const { data: cartCount } = useCartCount();

  // Only call useUserProfile after auth is initialized
  const { data: userProfile, isLoading: isUserProfileLoading } = useUserProfile({
    enabled: isAuthReady,
  });
  const isLoggedIn = !!userProfile;
  const appInstallationControllerRef = useRef<AbortController | null>(null);

  // Get product data from cache without triggering a fetch
  const { data: product, isLoading: isProductLoading } = useQuery({
    ...brandclubProductQueryOptions(currentStacklineSku),
    enabled: false,
    staleTime: Infinity,
  });

  // Get checkout bar treatments from cache without triggering a fetch
  const { data: checkoutBarTreatments } = useQuery({
    ...appConfigQueryOptions(),
    enabled: false,
    staleTime: Infinity,
    select: (data) => data.domainConfig?.abTestingTreatmentResults,
  });

  const trackPageView = useTrackViewEvent(AnalyticsTrackingEvent.PAGE_VIEW);
  const [_, appendCheckoutBarActionDetails] = useTrackActions();

  // Every adoption of a host SDK re-runs the identity effect below. On a host
  // that owns analytics this bundle starts no SDK of its own, so identity has
  // to wait for the instance the host hands over.
  const hostMixpanelAdoptions = useSyncExternalStore(
    subscribeHostMixpanel,
    getHostMixpanelAdoptions,
    getHostMixpanelAdoptions
  );

  // Update Mixpanel identity when user data is available
  useEffect(() => {
    if (!isAuthReady || isUserProfileLoading) {
      return;
    }

    try {
      AnalyticsService.setCurrentVisitorTracking(visitorTracking);
      if (visitorTracking?.uniqueVisitorId) {
        dispatch(updateVisitorTracking(removeFunctionFields(visitorTracking)));
      }
      // Mixpanel needs an SDK that was started. Without one, identify() reads
      // the hooks of an object that was never initialized and throws. The host
      // supplies the SDK on such a host, and its arrival re-runs this effect.
      if (!AnalyticsService.isMixpanelInitialized()) {
        logger.debug('Mixpanel identity waits for the host SDK');
        return;
      }
      AnalyticsService.setMixpanelUserIdentity(visitorTracking, userProfile as UserProfile | null);
    } catch (e) {
      logger.error('Error updating Mixpanel identity', e);
    }
  }, [dispatch, isAuthReady, isUserProfileLoading, visitorTracking, userProfile, hostMixpanelAdoptions]);

  // Track page views - but only after we know auth status
  useEffect(() => {
    if (!isAuthReady || (currentStacklineSku && isProductLoading)) {
      return;
    }

    const checkoutBarVisible =
      checkoutBarTreatments?.['brandclub_dtc_checkout_bar']?.treatment !== 'off' && (currentStacklineSku || !!cartCount)
        ? true
        : undefined;
    const checkoutView = checkoutBarVisible ? 'checkout_bar' : 'inpage_checkout';
    const productInfo = product
      ? {
          pageType: 'Catalog' as const,
          pageId: product.stacklineSku,
          retailerId: product.retailerId,
          retailPrice: product.retailPrice,
          retailerSku: product.retailerSku,
          stacklineSku: product.stacklineSku,
          totalRewardAmount: product.totalRewardAmount,
          retailerProductPageUrl: product.retailerProductPageUrl,
          title: product.title,
          checkoutEligibility: product.checkoutEligibility,
          matchedProducts: product.matchedProducts,
        }
      : {};

    // The `track` wrapper no-ops when the host owns analytics (suppressAnalytics),
    // so this call is safe to make unconditionally. Action context below still
    // primes regardless (it's not an emit).
    trackPageView(
      {
        ...productInfo,
        checkoutView,
        checkoutBarTreatments,
        isAuthenticated: isLoggedIn,
      },
      {},
    );

    appendCheckoutBarActionDetails({
      ...productInfo,
      isAuthenticated: isLoggedIn,
    });
  }, [
    currentStacklineSku,
    isLoggedIn,
    product,
    isProductLoading,
    isAuthReady,
    cartCount,
    checkoutBarTreatments,
  ]);

  // Handle app installation info with proper abort control
  useEffect(() => {
    if (!isAuthReady || isUserProfileLoading || !userProfile?.userId) {
      return;
    }

    // Abort any previous initialization
    appInstallationControllerRef.current?.abort('New initialization');

    const controller = new AbortController();
    appInstallationControllerRef.current = controller;

    initAppInstallationInfo(userProfile.userId, controller.signal);

    return () => {
      controller.abort('Component unmount');
      if (appInstallationControllerRef.current === controller) {
        appInstallationControllerRef.current = null;
      }
    };
  }, [userProfile?.userId, isAuthReady]);

  return null;
};

/**
 * `reloadOnSignOut` is false on a host page that reads the sign-in change itself. The Pages storefront
 * refreshes its own data in place, so a reload here would be a second act.
 */
export const AppInitializer: React.FC<PropsWithChildren<{ reloadOnSignOut?: boolean }>> = ({
  children,
  reloadOnSignOut = true,
}) => {
  const queryClient = useQueryClient();
  const persistedQueryManager = usePersistedQueryManager();
  const [isAuthReady, setIsAuthReady] = useState(false);
  const initStartedRef = useRef({
    auth: false,
    amplifyHub: false,
  });
  // Initialize Mixpanel immediately
  useEffect(() => {
    initializeMixpanel(HOST_DOMAIN);
  }, []);

  // Load core configurations
  const { data: brandAppConfig } = useBrandAppConfigWithFallback({
    notifyOnChangeProps: [],
  });
  const { data: appConfig, isError: appConfigFailed } = useAppConfig();

  const { refetch: fetchUserProfile } = useUserProfile({
    enabled: false, // we don't want to fetch the user profile until auth is ready
  });

  // Auth starts after the app config loads. The config read settles the V2 bootstrap that sign-in setup reads.
  useEffect(() => {
    const authConfig = appConfig?.authConfig;
    // Without an auth config no session can exist. Profile reads can answer "signed out" now.
    if (appConfigFailed || (appConfig && !authConfig)) {
      markAuthReady();
      return;
    }
    if (!authConfig || !brandAppConfig || initStartedRef.current.auth) {
      return;
    }
    initStartedRef.current.auth = true;
    const initialize = async () => {
      try {
        await initializeAmplifyAuth(authConfig, appConfig?.domainConfig?.domainName);
        logger.debug('Amplify auth initialized successfully');
      } catch (error) {
        logger.error('Auth initialization failed', error);
        return;
      } finally {
        // Profile reads wait for this signal. Send it before the fetch below, or that fetch waits for itself.
        markAuthReady();
      }

      await fetchUserProfile().catch(() => {
        logger.debug('User not logged in');
      });
    };

    initialize().then(() => {
      setIsAuthReady(true);
    });
  }, [appConfig, appConfigFailed, brandAppConfig]);

  // Set up auth event listeners
  useEffect(() => {
    if (initStartedRef.current.amplifyHub) {
      return;
    }
    initStartedRef.current.amplifyHub = true;
    const unsubscribe = AmplifyHub.listen('auth', ({ payload: { event, data } }) => {
      logger.debug('Amplify auth event received', event, data);
      if (event === 'signOut') {
        logger.debug('User signed out removing user profile from cache');
        persistedQueryManager.invalidateQueries({
          queryKey: userProfileQueryOptions().queryKey,
          type: 'all',
        });
        void dropCart(queryClient);
        if (reloadOnSignOut) window.location.reload();
      } else if (['signIn', 'signIn_Complete', 'signUp'].includes(event)) {
        logger.debug('User signed in fetching user profile');
        fetchUserProfile();
        void dropCart(queryClient);
      }
    });
    // A page the browser kept in memory holds the cart of the moment it was left.
    const onShow = (event: PageTransitionEvent) => {
      if (event.persisted) void dropCart(queryClient);
    };
    window.addEventListener('pageshow', onShow);
    logger.debug('Subscribed to Amplify auth events');
    return () => {
      logger.debug('Unsubscribing from Amplify auth events');
      unsubscribe();
      window.removeEventListener('pageshow', onShow);
      initStartedRef.current.amplifyHub = false;
    };
  }, [queryClient, reloadOnSignOut]);

  // Enable data queries as soon as configs are loaded (parallel to auth)
  const canLoadData = !!(brandAppConfig && appConfig);

  useQuery({
    ...appThemeQueryOptions(),
    notifyOnChangeProps: [],
    enabled: canLoadData,
  });

  useCurrentBrandclubProduct({
    notifyOnChangeProps: [],
    enabled: canLoadData,
  });

  useDtcSiteProduct({
    notifyOnChangeProps: [],
    enabled: canLoadData,
  });

  useQuery({
    ...activeCartsQueryOptions(),
    notifyOnChangeProps: [],
    enabled: canLoadData && isAuthReady,
  });

  // Handle page refresh
  useEffect(() => {
    if (!canLoadData) {
      return;
    }

    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    if (navigation?.type === 'reload') {
      persistedQueryManager.invalidateQueries({
        queryKey: activeCartsQueryOptions().queryKey,
        type: 'all',
      });
    }
  }, [persistedQueryManager, canLoadData]);

  // Don't render until configs are loaded
  if (!canLoadData) {
    return null;
  }

  return (
    <>
      <MixpanelTracking isAuthReady={isAuthReady} />
      {children}
    </>
  );
};
